import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Lock, Mail, X, CreditCard } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const { login, currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast({
        title: '¡Bienvenido!',
        description: 'Has iniciado sesión correctamente',
      });
      navigate('/dashboard');
    } else {
      toast({
        title: 'Error',
        description: result.error,
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/logo.png" 
            alt="NotiMax" 
            className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 rounded-2xl shadow-lg object-cover"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">NotiMax</h1>
          <p className="text-sm sm:text-base text-gray-600">Sistema inteligente de gestión de citas</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-3 sm:pb-4">
            <CardTitle className="text-xl sm:text-2xl font-bold text-center">Iniciar Sesión</CardTitle>
            <CardDescription className="text-center text-sm">
              Ingresa tus credenciales para acceder
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="password" className="text-sm">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Ingresa tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium py-5 sm:py-6 transition-all duration-300 shadow-lg hover:shadow-xl text-base"
                disabled={loading}
              >
                {loading ? 'Iniciando...' : 'Iniciar Sesión'}
              </Button>
            </form>

            <div className="mt-4 sm:mt-6 text-center space-y-3">
              <p className="text-xs sm:text-sm text-gray-600">
                ¿No tienes cuenta?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold">
                  Crear cuenta gratis
                </Link>
              </p>
              
              <button
                onClick={() => setShowPlansModal(true)}
                className="text-sm text-green-600 hover:text-green-700 font-semibold underline"
              >
                Ver Planes Premium →
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plans Modal */}
      {showPlansModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">🎉 Planes Premium</h2>
                <button
                  onClick={() => setShowPlansModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Comienza con 14 días de prueba gratis. Sin tarjeta de crédito requerida.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Monthly Plan */}
                <div className="border-2 border-blue-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Mensual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-blue-600">$18</span>
                      <span className="text-gray-500 ml-2">USD/mes</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">14 días de prueba gratis</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Hasta 3 dispositivos</span>
                    </li>
                  </ul>
                </div>

                {/* Annual Plan */}
                <div className="border-2 border-green-200 rounded-lg p-6 hover:border-green-500 transition-colors relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-green-500 text-white px-3 py-1">¡Ahorra $67!</Badge>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Anual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-green-600">$149</span>
                      <span className="text-gray-500 ml-2">USD/año</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Solo $12.42/mes</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">14 días de prueba gratis</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600 font-semibold">2 meses gratis</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="text-center mt-6">
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white px-8 py-3">
                    Comenzar Prueba Gratis
                  </Button>
                </Link>
                <p className="text-xs text-gray-500 mt-3">
                  💳 No se requiere tarjeta de crédito para la prueba gratuita
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;